import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';

import { ExternalLink } from '../external-link';

interface ExternalLinkIconProps {
  href: string;
  title?: string;
  disabled?: boolean;
  size?: number;
}

/**
 * ### ExternalLinkIcon
 *
 * @description
 * A reusable external link icon component that opens links in a new tab
 *
 * @example
 * <ExternalLinkIcon
 *   disabled={false}
 *   href="/admin/highlight-templates/question-designs/123"
 *   size={20}
 *   title="Open question design in new tab"
 * />
 *
 * @param props - Component props
 * @returns React component
 */
const ExternalLinkIcon = ({ href, title, disabled, size = 16 }: ExternalLinkIconProps) => {
  const { t } = useTranslation();

  const label = t(title || 'Open in new tab');

  if (disabled) {
    // A link that only looks unclickable is still focusable and still announced as a link; drop
    // the anchor entirely rather than neutralizing it with pointer-events.
    return <FiExternalLink color="var(--mantine-color-disabled-color)" size={size} aria-hidden />;
  }

  return (
    // The icon carries no text, so the link is named explicitly rather than by `title` alone.
    <ExternalLink href={href} title={label} aria-label={label}>
      <FiExternalLink color="var(--mantine-primary-color-filled)" size={size} aria-hidden />
    </ExternalLink>
  );
};

export default ExternalLinkIcon;
